import { createElement, useEffect, useLayoutEffect, useRef } from "react";

import { SectionRole } from "../../shared/types/roles";
import { truncate } from "../../shared/utils";

import { ChatMentionToast } from "./ChatMentionToast";
import { getChatMessageVisibility } from "./chat-message-visibility";
import { getMentionTexts } from "./mentions";

import { SecondaryOverlay } from "components/constants";
import type { ChatMessageType } from "components/server-types";
import { ChatWithLinesIcon } from "mds/icons";
import { store, useAppDispatch, useAppSelector } from "store";
import {
  filterChatMentionTargetsForAuthor,
  getChatMentionAuthorPermissions,
  selectChatMentionAuthorPermissionsByUserId,
  selectChatMentionTargetsForCurrentUser,
  selectCurrentClassUser,
  selectCurrentSectionId,
  selectCurrentSectionRole,
  selectCurrentUserId,
  selectUnseenChatMentionMessageIdsForCurrentSection,
  userSelectors,
} from "store/selectors";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { chatMentionsActions } from "store/slices/chat-mentions";
import { useListSelector } from "store/store-hooks";
import { toastInfoMessage } from "utils/alerts";
import { BROADCAST_TOPICS, useBroadcastListener } from "utils/broadcast";
import { rollbarAndLogError } from "utils/logger";
import { useOverlayControls } from "utils/routing/hooks/use-overlay-controls";
import { getUserFullName } from "utils/user-utils";

const CHAT_MESSAGE_RENDER_RETRY_COUNT = 30;

export const useChatMentionObserver = () => {
  const pendingMentionMessageIds = useRef(new Set<string>());
  const pendingVisibilityFrameIds = useRef(new Map<string, number>());
  const dispatch = useAppDispatch();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentSectionRole = useAppSelector(selectCurrentSectionRole);
  const currentUserIsInClassSession = useAppSelector(
    (state) => selectCurrentClassUser(state)?.is_in_class_session ?? false,
  );
  const currentUserIsObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentUserMentionTargets = useListSelector(selectChatMentionTargetsForCurrentUser, [
    "id",
    "kind",
    "mentionText",
    "mentionAliases",
  ]);
  const { isSelected: isChatOpen, togglePanel: toggleChatPanel } = useOverlayControls(
    SecondaryOverlay.ClassChat,
  );
  const chatControlsRef = useRef({ isChatOpen, toggleChatPanel });

  useLayoutEffect(() => {
    chatControlsRef.current = { isChatOpen, toggleChatPanel };
  }, [isChatOpen, toggleChatPanel]);

  useEffect(
    () => () => {
      for (const frameId of pendingVisibilityFrameIds.current.values()) {
        window.cancelAnimationFrame(frameId);
      }
      pendingVisibilityFrameIds.current.clear();
    },
    [],
  );

  useBroadcastListener(
    BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE,
    (message) => {
      if (message.type !== "model-update" || message.model !== "chat_messages") {
        return;
      }

      const chatMessage = message.data as Pick<ChatMessageType, "id">;
      if (!chatMessage.id) return;

      if (message.action === "delete") {
        pendingMentionMessageIds.current.delete(chatMessage.id);
        const frameId = pendingVisibilityFrameIds.current.get(chatMessage.id);
        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
          pendingVisibilityFrameIds.current.delete(chatMessage.id);
        }
        dispatch(
          chatMentionsActions.removeDeletedMention({
            messageId: chatMessage.id,
            sectionId: message.section_id,
          }),
        );
        return;
      }
      if (message.action !== "create") return;

      const createdChatMessage = message.data as ChatMessageType;
      if (
        createdChatMessage.section_id !== sectionId ||
        createdChatMessage.author_id === currentUserId ||
        (!currentUserIsInClassSession && !currentUserIsObservingClass) ||
        currentSectionRole === SectionRole.RECORDING_AGENT
      ) {
        return;
      }

      const state = store.getState();
      const authorPermissions = getChatMentionAuthorPermissions(
        selectChatMentionAuthorPermissionsByUserId(state),
        createdChatMessage.author_id,
      );
      const author = userSelectors.selectById(state, createdChatMessage.author_id);
      const eligibleMentionTexts = filterChatMentionTargetsForAuthor(
        currentUserMentionTargets,
        authorPermissions,
      ).flatMap(getMentionTexts);
      if (!eligibleMentionTexts.length) return;

      pendingMentionMessageIds.current.add(createdChatMessage.id);
      import("./markdown-mentions")
        .then(({ findVisibleMentionsInMarkdown }) => {
          if (!pendingMentionMessageIds.current.delete(createdChatMessage.id)) return;

          const visibleMentions = findVisibleMentionsInMarkdown(
            createdChatMessage.text,
            eligibleMentionTexts,
          );
          if (!visibleMentions.length) return;

          dispatch(
            chatMentionsActions.addUnseenMention({
              messageId: createdChatMessage.id,
              sectionId: createdChatMessage.section_id,
            }),
          );

          const openMention = () => {
            dispatch(
              chatMentionsActions.requestOldestUnseenMention({
                sectionId: createdChatMessage.section_id,
              }),
            );
            if (!chatControlsRef.current.isChatOpen) {
              chatControlsRef.current.toggleChatPanel();
            }
          };

          const showToast = () =>
            toastInfoMessage(
              createElement(ChatMentionToast, {
                authorName: author ? getUserFullName(author) : null,
                messagePreview: truncate(createdChatMessage.text, 120),
                onClick: openMention,
              }),
              { icon: () => createElement(ChatWithLinesIcon), role: "status" },
            );

          if (!chatControlsRef.current.isChatOpen) {
            showToast();
            return;
          }

          const scheduleVisibilityCheck = (renderAttempt = 0) => {
            const frameId = window.requestAnimationFrame(() => {
              pendingVisibilityFrameIds.current.delete(createdChatMessage.id);
              const mentionIsStillUnseen = selectUnseenChatMentionMessageIdsForCurrentSection(
                store.getState(),
              ).includes(createdChatMessage.id);
              if (!mentionIsStillUnseen) return;

              const messageVisibility = getChatMessageVisibility(createdChatMessage.id);
              if (
                chatControlsRef.current.isChatOpen &&
                messageVisibility === null &&
                renderAttempt < CHAT_MESSAGE_RENDER_RETRY_COUNT
              ) {
                scheduleVisibilityCheck(renderAttempt + 1);
                return;
              }
              if (chatControlsRef.current.isChatOpen && messageVisibility) return;
              showToast();
            });
            pendingVisibilityFrameIds.current.set(createdChatMessage.id, frameId);
          };
          scheduleVisibilityCheck();
        })
        .catch((error) => {
          pendingMentionMessageIds.current.delete(createdChatMessage.id);
          rollbarAndLogError("Loading chat mention parser failed", error);
        });
    },
    [
      currentSectionRole,
      currentUserIsInClassSession,
      currentUserIsObservingClass,
      currentUserId,
      currentUserMentionTargets,
      dispatch,
      sectionId,
    ],
  );
};
